/**
 * TokenRefreshService - Handles token refresh outside of React/Apollo hooks
 * This service can be used in Apollo links or other non-React contexts
 */

import { GRAPHQL_ENDPOINT } from '../constants/graphqlEndpoint';
import { TokenService } from './TokenService';

// The same API key used for BankID authentication
const ALLKORT_API_KEY = '83740278-c4d7-42b1-86bc-090b3bf119f6';
const REFRESH_REQUEST_TIMEOUT_MS = 10000;

// Refresh token mutation as a raw string (same as in BankIdQueries.ts)
const REFRESH_TOKEN_MUTATION = `
  mutation TokenByRefreshToken($input: TokenByRefreshTokenInput!) {
    tokenByRefreshToken(input: $input) {
      bearerToken {
        customerId
        refreshToken
        refreshTokenExpiration
        token
        tokenExpiration
      }
    }
  }
`;

// Track if a refresh is in progress to prevent multiple simultaneous refreshes
let refreshPromise: Promise<boolean> | null = null;

/**
 * Check if the access token needs refresh
 * @param thresholdMinutes Minutes before expiration to trigger refresh (default 5)
 */
export const needsTokenRefresh = (thresholdMinutes: number = 5): boolean => {
  const token = TokenService.getToken();
  
  // No token at all - need refresh if we have a valid refresh token
  if (!token) {
    const hasRefresh = TokenService.hasValidRefreshToken();
    console.log(`No access token, has valid refresh token: ${hasRefresh}`);
    return hasRefresh;
  }
  
  // Check if token is expiring soon
  const tokenExpiry = TokenService.getTokenExpiry();
  if (!tokenExpiry) {
    return false;
  }
  
  const now = new Date();
  const minutesUntilExpiry = (tokenExpiry.getTime() - now.getTime()) / (1000 * 60);
  
  const needsRefresh = minutesUntilExpiry <= thresholdMinutes;
  
  if (needsRefresh) {
    // Only attempt a refresh if we actually have a refresh token to use
    if (!TokenService.hasValidRefreshToken()) {
      return false;
    }
    console.log(`Token expires in ${minutesUntilExpiry.toFixed(1)} minutes (threshold: ${thresholdMinutes})`);
  }
  
  return needsRefresh;
};

/**
 * Refresh the access token using the stored refresh token
 * Returns true if successful, false otherwise
 * Prevents multiple simultaneous refresh attempts
 */
export const refreshAccessToken = async (): Promise<boolean> => {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    console.log('Token refresh already in progress, waiting...');
    return refreshPromise;
  }
  
  // Start new refresh
  refreshPromise = doRefreshToken();
  
  try {
    const result = await refreshPromise;
    return result;
  } finally {
    refreshPromise = null;
  }
};

/**
 * Internal function that performs the actual token refresh
 */
const doRefreshToken = async (): Promise<boolean> => {
  const currentRefreshToken = TokenService.getRefreshToken();
  
  if (!currentRefreshToken) {
    console.error('No refresh token available');
    return false;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_REQUEST_TIMEOUT_MS);
  
  try {
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify({
        query: REFRESH_TOKEN_MUTATION,
        variables: {
          input: {
            key: ALLKORT_API_KEY,
            refreshToken: currentRefreshToken,
          },
        },
      }),
    });
    
    if (!response.ok) {
      console.error(`Refresh request failed: ${response.status} ${response.statusText}`);
      return false;
    }
    
    const result = await response.json();
    
    // Check for GraphQL errors
    if (result.errors && result.errors.length > 0) {
      console.error('GraphQL errors during refresh:', result.errors);
      
      // Check if refresh token is invalid - clear tokens to force re-login
      const isInvalidToken = result.errors.some((e: any) =>
        e.message?.toLowerCase().includes('invalid refresh token') ||
        e.message?.toLowerCase().includes('expired') ||
        e.extensions?.code === 'AUTH_NOT_AUTHENTICATED'
      );
      
      if (isInvalidToken) {
        console.log('Refresh token is invalid, clearing tokens and forcing re-login...');
        TokenService.clearToken();
      }
      
      return false;
    }
    
    // Extract new token data
    const newTokenData = result.data?.tokenByRefreshToken?.bearerToken;
    
    if (!newTokenData) {
      console.error('No bearer token in refresh response');
      return false;
    }
    
    // Get current customer info from storage to preserve it
    // First try the cookie (if not expired), then fall back to refresh-session data
    const currentCustomer = TokenService.getCustomer();
    const storedCustomer = TokenService.getStoredCustomer();
    
    // Create a BearerToken object compatible with TokenService.saveToken
    const bearerToken = {
      token: newTokenData.token,
      tokenExpiration: newTokenData.tokenExpiration,
      refreshToken: newTokenData.refreshToken,
      refreshTokenExpiration: newTokenData.refreshTokenExpiration,
      customerId: newTokenData.customerId,
      customer: currentCustomer || storedCustomer || {
        name: 'Kund', // Swedish fallback when older session data has no customer name
        customerNumber: newTokenData.customerId,
      },
    };
    
    // Save the new tokens
    TokenService.saveToken(bearerToken);
    
    console.log('[TokenRefreshService] Token refreshed successfully!');
    console.log(`   New access token expires: ${newTokenData.tokenExpiration}`);
    console.log(`   New refresh token expires: ${newTokenData.refreshTokenExpiration}`);
    
    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Ensure we have a valid access token before making a request
 * Refreshes if needed
 * @param thresholdMinutes Minutes before expiration to trigger refresh
 */
export const ensureValidToken = async (thresholdMinutes: number = 5): Promise<boolean> => {
  if (needsTokenRefresh(thresholdMinutes)) {
    console.log('[TokenRefreshService] Token needs refresh before request');
    return await refreshAccessToken();
  }
  return true;
};
